import { useState } from "react";
import { MailWarning } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Persistent verification banner shown to logged-in users whose email
 * is not yet verified.  Copy is fully localised (EN + AR) and RTL is
 * inherited from the parent locale wrapper.
 *
 * Admins are auto-verified at seed time and never see this banner.
 * The Resend action reuses the existing
 * `POST /api/auth/resend-verification` endpoint — no new endpoint,
 * no bypass of server-side cooldown or rate limits.
 */
export default function VerifyEmailBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [checking, setChecking] = useState(false);

  if (!user || user.role === "admin" || user.email_verified) return null;

  const onResend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      setSent(true);
      toast.success(t("verifyEmail.resendToast"));
    } catch (_) {
      // Enumeration-safe on the server: show the same success toast.
      setSent(true);
      toast.success(t("verifyEmail.resendToast"));
    } finally {
      setSending(false);
    }
  };

  // "I've verified — Refresh" fallback for the customer whose original
  // tab still shows the banner because they verified in another browser
  // / webview.  Refetches /auth/me and updates AuthContext via the
  // existing localStorage + `djspy-user-refreshed` bridge.
  const onRefresh = async () => {
    setChecking(true);
    try {
      const r = await api.get("/auth/me");
      if (r?.data) {
        localStorage.setItem("djspy_user", JSON.stringify(r.data));
        window.dispatchEvent(new Event("djspy-user-refreshed"));
        if (r.data.email_verified) {
          toast.success(t("verifyEmail.refreshedVerified"));
        } else {
          toast.info(t("verifyEmail.stillPending"));
        }
      }
    } catch (_) {
      toast.error(t("verifyEmail.refreshFailed"));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      data-testid="verify-email-banner"
      className="border border-red-brand/40 bg-red-brand/10 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 mb-6"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <MailWarning size={22} className="text-red-brand shrink-0 mt-0.5" />
        <div>
          <div
            className="text-white font-semibold"
            data-testid="verify-email-banner-title"
          >
            {t("verifyEmail.bannerTitle")}
          </div>
          <p className="text-white/70 text-sm mt-1">
            {t("verifyEmail.bannerBody")}{" "}
            <strong className="text-white break-all">{user.email}</strong>
          </p>
        </div>
      </div>
      <div className="shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <button
          onClick={onRefresh}
          disabled={checking}
          data-testid="verify-refresh-btn"
          className="btn-ghost !py-2 !px-3 text-xs whitespace-nowrap disabled:opacity-60"
        >
          {checking ? t("verifyEmail.refreshChecking") : t("verifyEmail.refreshButton")}
        </button>
        <button
          onClick={onResend}
          disabled={sending || sent}
          data-testid="verify-resend-btn"
          className="btn-primary whitespace-nowrap"
        >
          {sent
            ? t("verifyEmail.resendSent")
            : sending
            ? t("verifyEmail.resendSending")
            : t("verifyEmail.resendButton")}
        </button>
      </div>
    </div>
  );
}
